import React, { useState, useEffect, useRef } from "react";
import { styled } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import { css } from "@emotion/react";
import Button from "../components/Button";
import { Close, ArrowForwardIos } from "@mui/icons-material";
import Alert from "@mui/material/Alert";

const ModalBox = styled(Box)(({ theme }) => ({
  position: "absolute",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[24],
  width: "max-content",
  minWidth: "min(400px, calc(100vw - 32px))",
  maxWidth: "calc(100vw - 32px)",
}));

const ModalInner = styled("div")(({ theme }) => ({
  position: "relative",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  paddingBottom: theme.spacing(5),
  paddingTop: theme.spacing(3.125),
  paddingRight: theme.spacing(3.75),
  paddingLeft: theme.spacing(3.75),
  width: "100%",
}));

const Text = styled("p")(({ theme }) => ({
  fontSize: "1.125rem",
  fontFamily: theme.typography.body1.fontFamily,
  color: theme.palette.text.primary,
  margin: theme.spacing(2, 0),
  textAlign: "center",
}));

const CloseButton = styled(Close)(({ theme }) => ({
  position: "absolute",
  top: theme.spacing(1.25),
  right: theme.spacing(1.25),
  color: theme.palette.text.secondary,
  width: "25px",
  height: "25px",
  cursor: "pointer",
  "&:hover": {
    color: theme.palette.text.primary,
  },
}));

// Slide to confirm button components
const SlideContainer = styled("div")(({ theme }) => ({
  position: "relative",
  width: "min(320px, 100%)",
  height: "56px",
  backgroundColor: theme.palette.grey[200],
  borderRadius: theme.shape.borderRadius * 3.5,
  overflow: "hidden",
  marginTop: theme.spacing(3),
  cursor: "pointer",
  userSelect: "none",
}));

const SlideTrack = styled("div")(({ theme }) => ({
  position: "absolute",
  top: 0,
  left: 0,
  height: "100%",
  backgroundColor: theme.palette.error.main,
  borderRadius: theme.shape.borderRadius * 3.5,
  transition: "width 0.1s ease-out",
  opacity: 0.9,
}));

const SlideThumb = styled("div")(({ theme, position }) => ({
  position: "absolute",
  top: "50%",
  transform: "translateY(-50%)",
  left: `${position}px`,
  width: "48px",
  height: "48px",
  backgroundColor: theme.palette.background.paper,
  borderRadius: "50%",
  boxShadow: theme.shadows[2],
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  transition: "left 0.1s ease-out, transform 0.1s ease-out",
  zIndex: 2,
  "&:hover": {
    boxShadow: theme.shadows[4],
  },
}));

const SlideText = styled("span")(({ theme, faded }) => ({
  position: "absolute",
  left: "50%",
  top: "50%",
  transform: "translate(-50%, -50%)",
  fontSize: "1rem",
  fontFamily: theme.typography.button.fontFamily,
  fontWeight: 500,
  color: theme.palette.text.primary,
  opacity: faded ? 0.3 : 0.7,
  transition: "opacity 0.3s ease",
  pointerEvents: "none",
}));

// NOTE: this is ConfirmAction's OWN private slider — a different component from
// the shared src/components/SlideToConfirm.jsx (different props, different DOM
// tree). Keep the two in sync only on their test handles, not their internals.
const SlideToConfirm = ({
  onConfirm,
  actionText = "Sign Out",
  testId = "slide-to-confirm",
  ariaLabel,
  disabled = false,
}) => {
  const [position, setPosition] = useState(4);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef(null);
  // Derive geometry from the rendered track: the container is
  // min(320px, 100%), so a fixed 268px max would be unreachable on
  // narrow tracks and the slide could never complete.
  const getMaxPosition = () =>
    (containerRef.current?.getBoundingClientRect().width ?? 320) - 52; // thumb 48 + padding 4

  const handleStart = (clientX) => {
    if (disabled) return;
    setIsDragging(true);
  };

  const handleMove = (clientX, rect) => {
    if (!isDragging || disabled) return;

    const maxPosition = getMaxPosition();
    const x = clientX - rect.left - 24; // 24 is half thumb width
    const newPosition = Math.max(4, Math.min(x, maxPosition));
    setPosition(newPosition);

    if (newPosition >= maxPosition * 0.9) {
      handleConfirm();
    }
  };

  const handleEnd = () => {
    if (position < getMaxPosition() * 0.9) {
      setPosition(4);
    }
    setIsDragging(false);
  };

  const handleConfirm = () => {
    if (disabled) return;
    setPosition(getMaxPosition());
    setIsDragging(false);
    setTimeout(() => {
      onConfirm();
    }, 200);
  };

  const handleMouseDown = (e) => handleStart(e.clientX);
  const handleMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    handleMove(e.clientX, rect);
  };
  const handleMouseUp = () => handleEnd();

  const handleTouchStart = (e) => handleStart(e.touches[0].clientX);
  const handleTouchMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    handleMove(e.touches[0].clientX, rect);
  };
  const handleTouchEnd = () => handleEnd();

  useEffect(() => {
    const handleGlobalMouseUp = () => {
      if (isDragging) {
        handleEnd();
      }
    };

    const handleGlobalTouchEnd = () => {
      if (isDragging) {
        handleEnd();
      }
    };

    if (isDragging) {
      document.addEventListener("mouseup", handleGlobalMouseUp);
      document.addEventListener("touchend", handleGlobalTouchEnd);
    }

    return () => {
      document.removeEventListener("mouseup", handleGlobalMouseUp);
      document.removeEventListener("touchend", handleGlobalTouchEnd);
    };
  }, [isDragging, position]);

  const progress = position / getMaxPosition();

  return (
    <SlideContainer
      ref={containerRef}
      data-testid={testId}
      role="button"
      aria-label={ariaLabel || `Slide to ${actionText}`}
      data-confirm-label={actionText}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}>
      <SlideTrack style={{ width: `${position + 48}px` }} />
      <SlideThumb position={position}>
        <ArrowForwardIos fontSize="small" />
      </SlideThumb>
      <SlideText faded={progress > 0.3}>Slide to {actionText}</SlideText>
    </SlideContainer>
  );
};

/**
 * Test/a11y handles (all optional, defaulted so every call site gets one):
 *   testId        - data-testid + role="dialog" on the modal surface
 *   ariaLabel     - dialog accessible name; falls back to the string children,
 *                   then dataForAction.actionText, then a generic label
 *   confirmTestId - the confirm control (the Button, or the slider when
 *                   `useSlider`); defaults to `${testId}-button-confirm`
 *   closeTestId   - the corner close icon; defaults to `${testId}-button-close`
 * The confirm control keeps its visible text as its accessible name, so
 * existing text-based locators keep working.
 */
export const ConfirmAction = ({
  callback,
  children,
  openState,
  handleCloseState,
  dataForAction,
  useSlider = false,
  testId = "confirm-action",
  ariaLabel,
  confirmTestId,
  closeTestId,
}) => {
  const [pending, setPending] = useState(false);
  const [actionError, setActionError] = useState(null);

  // Reopening after a failure must not inherit the previous attempt's state.
  useEffect(() => {
    if (openState) {
      setPending(false);
      setActionError(null);
    }
  }, [openState]);

  // The dialog used to close first and fire `callback` into the void, so a
  // slow action (a cold-starting callable can take most of a minute) left the
  // operator with no signal at all: the row sat there, then vanished. Hold the
  // dialog open and own the pending state here, so all call sites get honest
  // feedback without each one reimplementing it.
  const handleConfirm = async () => {
    if (pending) return;
    setActionError(null);
    setPending(true);
    try {
      await callback(dataForAction);
      handleCloseState();
    } catch (err) {
      // Callers that surface their own errors resolve normally and never reach
      // here; this is the backstop for the ones that throw.
      setActionError(
        err?.message || "That did not go through. Please try again.",
      );
    } finally {
      setPending(false);
    }
  };

  const confirmHandle = confirmTestId || `${testId}-button-confirm`;
  const closeHandle = closeTestId || `${testId}-button-close`;
  const dialogLabel =
    ariaLabel ||
    (typeof children === "string" ? children : dataForAction?.actionText) ||
    "Confirm action";

  return (
    <Modal open={openState} onClose={pending ? undefined : handleCloseState}>
      <ModalBox
        data-testid={testId}
        role="dialog"
        aria-modal="true"
        aria-label={dialogLabel}>
        {dataForAction !== undefined && dataForAction !== null && (
          <ModalInner>
            <CloseButton
              onClick={pending ? undefined : handleCloseState}
              data-testid={closeHandle}
              role="button"
              aria-label="Close"
              aria-hidden={false}
            />
            <Text>{children}</Text>

            {actionError && (
              <Alert
                severity="error"
                data-testid={`${testId}-error`}
                sx={{ marginTop: 2 }}>
                {actionError}
              </Alert>
            )}

            {useSlider ? (
              <SlideToConfirm
                onConfirm={handleConfirm}
                actionText={dataForAction.actionText || "Confirm"}
                testId={confirmHandle}
                disabled={pending}
              />
            ) : (
              <Button
                variant="contained"
                onClick={handleConfirm}
                data-testid={confirmHandle}
                processing={pending}
                aria-busy={pending}
                sx={{
                  minWidth: "200px",
                  marginTop: 2,
                }}>
                {`Confirm ${dataForAction.actionText || children}`}
              </Button>
            )}
          </ModalInner>
        )}
      </ModalBox>
    </Modal>
  );
};

export const filterStyle = (props) => {
  if (props.blur) {
    return css`
      filter: blur(20px);
    `;
  } else if (props.cancelAnimate) {
    return css`
      filter: none;
    `;
  } else {
    return css`
      filter: none;
      transition: filter 0.3s ease-in;
    `;
  }
};

export const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};
